import { asAbsoluteURL } from '@commons/helpers/url';
import type { LOCALES } from '@commons/i18n';
import type { DrupalNodePage } from '@commons/types/drupal';
import { API_WEBSITE } from '@commons/utils/constants';

/**
 * Type definition for a site alert from Drupal.
 */

export type SiteAlert = DrupalNodePage & {
  body: string;
  bannerTitle: string;
  bannerContent?: string;
};

/**
 * Fetch the site alerts published in Drupal for a given website and locale.
 *
 * This service should not be cached, as the alerts are time-sensitive.
 *
 * Unlike other services, this function can be called from client components.
 * This implies that the `fetch` function used here is the one from the browser and not the one extended by Next.js.
 * Therefore, options like `next.revalidate` are not available.
 *
 * @param locale - The current locale.
 *
 * @returns A promise that resolves to an array of alerts.
 */

export const fetchAlerts = async (locale: LOCALES): Promise<SiteAlert[]> => {
  const endpoint = `${locale}/api/pure/v1/alerts/${API_WEBSITE}?_format=json`;

  const url = asAbsoluteURL(endpoint, { scheme: 'https' });

  const res = await fetch(url);

  if (!res.ok) {
    console.error(`Failed to fetch alerts: ${res.statusText}`);
    return [];
  }

  return await res.json();
};
